import { useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import clsx from "clsx";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

import Form from "../base/Form/Form";
import { Button } from "../base/Button";
import { Select } from "../base/Select";
import { TextField } from "../base/TextField";
import { Toggle } from "../base/Toggle";
import { Alert } from "../base/Alert";
import { FormErrors } from "../base/Form/FormErrors";
import { FormToggle } from "../base/Form/FormToggle";
import { FormTextField } from "../base/Form/FormTextField";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { ICustomerSetting, InputType, SettingType } from "../../models/CustomerSettings/CustomerSetting";
import useCustomerSettingsMutations from "../../data/CustomerSetting/useCustomerSettingsMutations";
import useCustomerSettings from "../../data/CustomerSetting/useCustomerSettings";
import { CreateUpdateCustomerSetting } from "../../models/CustomerSettings/CreateUpdateCustomerSetting";
import { FormSelect } from "../base/Form/FormSelect";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";
import useSettingDefinitions from "../../data/SettingDefinition/useSettingDefinitions";
import { ISettingDefinition } from "../../models/SettingDefinition/SettingDefinition";

interface CreateUpdateCustomerSettingModalProps extends ModalControlProps {
  customerId: string;
  setting?: ICustomerSetting;
}

// A setting that has been ticked in the catalog, along with the values it will be created with.
interface SelectedSettingDraft {
  value: string;
  isVisibleToCustomer: boolean;
}

export function CreateUpdateCustomerSettingModal({
  open,
  setOpen,
  customerId,
  setting,
}: CreateUpdateCustomerSettingModalProps) {
  const { createMany, update } = useCustomerSettingsMutations(customerId);
  const { settingDefinitions } = useSettingDefinitions();
  const { customerSettings } = useCustomerSettings(customerId);

  const [selections, setSelections] = useState<Record<string, SelectedSettingDraft>>({});
  const [valueErrors, setValueErrors] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");

  const inputTypeOptions = useMemo(() => enumToSelectOptions(InputType), []);
  const settingTypeOptions = useMemo(() => enumToSelectOptions(SettingType), []);

  // Settings the customer already has, so we can flag them as enabled.
  const existingKeys = useMemo(
    () => new Set(customerSettings.data?.map((s) => s.key) ?? []),
    [customerSettings.data]
  );

  // The whole catalog (minus archived), so we can show enabled + available together.
  const catalogDefinitions = useMemo(
    () =>
      (settingDefinitions.data ?? [])
        .filter((d) => !d.isArchived)
        .sort((a, b) =>
          (a.label || a.key).localeCompare(b.label || b.key)
        ),
    [settingDefinitions.data]
  );

  const visibleDefinitions = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) return catalogDefinitions;

    return catalogDefinitions.filter(
      (d) =>
        d.key.toLowerCase().includes(term) ||
        (d.label ?? "").toLowerCase().includes(term)
    );
  }, [catalogDefinitions, search]);

  const selectedDefinitions: ISettingDefinition[] = useMemo(
    () => catalogDefinitions.filter((d) => !!selections[d.id]),
    [catalogDefinitions, selections]
  );

  const handleClose = () => {
    setSelections({});
    setValueErrors({});
    setSearch("");
    setOpen(false);
  };

  const toggleDefinition = (definition: ISettingDefinition) => {
    setSelections((current) => {
      const next = { ...current };

      if (next[definition.id]) {
        delete next[definition.id];
      } else {
        next[definition.id] = {
          value: definition.inputType === InputType.Toggle ? "false" : "",
          isVisibleToCustomer: definition.defaultVisibleToCustomer,
        };
      }

      return next;
    });

    setValueErrors((current) => {
      const next = { ...current };
      delete next[definition.id];
      return next;
    });
  };

  const updateSelection = (
    definitionId: string,
    changes: Partial<SelectedSettingDraft>
  ) => {
    setSelections((current) => ({
      ...current,
      [definitionId]: { ...current[definitionId], ...changes },
    }));
  };

  const createSettings = () => {
    const errors: Record<string, string> = {};

    selectedDefinitions.forEach((definition) => {
      const draft = selections[definition.id];
      const validation = validateValue(draft.value, definition.inputType);

      if (definition.required && !draft.value) {
        errors[definition.id] = "Value is required";
      } else if (validation !== true) {
        errors[definition.id] = validation;
      }
    });

    setValueErrors(errors);

    if (Object.keys(errors).length > 0) return;

    createMany.mutate(
      selectedDefinitions.map((definition) => ({
        key: definition.key,
        value: selections[definition.id].value,
        isVisibleToCustomer: selections[definition.id].isVisibleToCustomer,
        settingType: definition.settingType,
        required: definition.required,
        inputType: definition.inputType,
      })),
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const updateSetting = (data: CreateUpdateCustomerSetting) => {
    var hack: any = data;
    hack.settingType = parseInt(hack.settingType);
    hack.inputType = parseInt(hack.inputType);
    update.mutate(
      {
        ...hack,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const validateValue = (value: string, inputType: number) => {
    if (inputType === InputType.Toggle && value !== "true" && value !== "false") {
      return "Toggle value must be either 'true' or 'false'";
    }
    if (inputType === InputType.Number && isNaN(Number(value))) {
      return "Value must be a valid number";
    }
    return true;
  };

  return (
    <Modal open={open} setOpen={handleClose} width={setting ? "!w-1/3" : "!w-1/2"}>
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-4 text-gray-900">
            {setting ? "Edit Setting" : "Add Settings"}
          </h2>
        </div>

        {setting ? (
          <Form<CreateUpdateCustomerSetting>
            defaultValues={setting}
            onSubmit={updateSetting}
            className="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-4"
          >
            {({ register, control, watch }: UseFormReturn<CreateUpdateCustomerSetting>) => {
              const inputType = Number(watch("inputType"));

              return (
                <>
                  <div className="-m-4 md:col-span-2">
                    <FormErrors />
                  </div>
                  <div className="mb-4 grid grid-cols-1 gap-y-4 md:col-span-2">
                    <div>
                      <FormTextField {...register("key")} label="Key" />
                      <p className="text-xs text-gray-500 mt-2">
                        {"Use the format <GROUP>:<KEY> if you want to add the Setting to a group."}
                      </p>
                    </div>

                    <div>
                      <FormSelect {...register("inputType")} label="Input Type">
                        {inputTypeOptions.map((opt) => (
                          <option key={opt.label} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </FormSelect>
                    </div>

                    <div>
                      <FormTextField
                        {...register("value", {
                          validate: (value) => validateValue(value, inputType),
                        })}
                        label="Value"
                      />
                      {inputType === InputType.Toggle && (
                        <p className="text-xs text-gray-500 mt-2">
                          For Toggle input type, value must be either "true" or "false".
                        </p>
                      )}
                    </div>

                    <div>
                      <FormSelect {...register("settingType")} label="Setting Type">
                        <option value={SettingType.General}>General</option>
                        <option value={SettingType.Shipping}>Shipping</option>
                      </FormSelect>
                    </div>

                    <div className="mt-2 flex justify-between space-x-2">
                      <FormToggle
                        {...register("isVisibleToCustomer")}
                        control={control}
                        toggleProps={{ label: "Visible to Customer?" }}
                      />
                      <FormToggle
                        {...register("required")}
                        control={control}
                        toggleProps={{ label: "Required?" }}
                      />
                    </div>
                  </div>
                  <Button type="submit">Save</Button>
                  <Button type="reset" variant="outlined">
                    Reset
                  </Button>
                </>
              );
            }}
          </Form>
        ) : (
          <div className="grid grid-cols-1 gap-y-4">
            {Object.keys(valueErrors).length > 0 && (
              <Alert
                severity="error"
                title="Validation Error"
                message="Fix the highlighted values before saving."
              />
            )}

            <div>
              <label className="text-sm font-medium opacity-80">Settings</label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Tick as many settings as you need, then give each one a value below.
                Settings already enabled for this customer are marked. Manage the catalog
                on the Settings page.
              </p>
              <TextField
                fullWidth
                small
                className="mb-2"
                placeholder="Search settings..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
              <div className="max-h-72 overflow-y-auto rounded border border-gray-200">
                <table className="min-w-full divide-y divide-gray-100 text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <span className="sr-only">Select</span>
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">
                        Setting
                      </th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">
                        Input Type
                      </th>
                      <th className="px-3 py-2 text-right font-medium text-gray-500">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {settingDefinitions.isLoading ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                          Loading settings...
                        </td>
                      </tr>
                    ) : catalogDefinitions.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                          No settings in the catalog
                        </td>
                      </tr>
                    ) : visibleDefinitions.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                          No settings match "{search}"
                        </td>
                      </tr>
                    ) : (
                      visibleDefinitions.map((definition) => {
                        const enabled = existingKeys.has(definition.key);
                        const isSelected = !!selections[definition.id];

                        return (
                          <tr
                            key={definition.id}
                            onClick={() => !enabled && toggleDefinition(definition)}
                            className={clsx(
                              enabled
                                ? "cursor-not-allowed bg-gray-50"
                                : "cursor-pointer hover:bg-orange-50",
                              isSelected && "bg-orange-100 hover:bg-orange-100"
                            )}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                readOnly
                                checked={isSelected}
                                disabled={enabled}
                                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-600 disabled:cursor-not-allowed"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">
                                {definition.label || definition.key}
                              </div>
                              {definition.label && (
                                <div className="font-mono text-xs text-gray-500 break-all">
                                  {definition.key}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-600">
                              {inputTypeOptions[definition.inputType]?.label ??
                                definition.inputType}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {enabled ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                  <CheckIcon className="h-3.5 w-3.5" />
                                  Enabled
                                </span>
                              ) : isSelected ? (
                                <span className="inline-flex items-center rounded-full bg-orange-600 px-2 py-0.5 text-xs font-medium text-white">
                                  Selected
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">Not enabled</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedDefinitions.length > 0 && (
              <div>
                <label className="text-sm font-medium opacity-80">
                  Values ({selectedDefinitions.length} selected)
                </label>
                <div className="mt-2 space-y-3">
                  {selectedDefinitions.map((definition) => {
                    const draft = selections[definition.id];
                    const error = valueErrors[definition.id];

                    return (
                      <div
                        key={definition.id}
                        className="rounded border border-gray-200 p-3"
                      >
                        <div className="flex items-start justify-between space-x-2">
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              {definition.label || definition.key}
                            </div>
                            <div className="font-mono text-xs text-gray-500 break-all">
                              {definition.key}
                            </div>
                          </div>
                          <Button
                            size="xs"
                            variant="outlined"
                            onClick={() => toggleDefinition(definition)}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>

                        {definition.description && (
                          <p className="mt-2 text-xs text-gray-600">
                            {definition.description}
                          </p>
                        )}

                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            {definition.inputType === InputType.Toggle ? (
                              <Select
                                fullWidth
                                label="Value"
                                value={draft.value}
                                onChange={(e) =>
                                  updateSelection(definition.id, {
                                    value: e.currentTarget.value,
                                  })
                                }
                              >
                                <option value="true">true</option>
                                <option value="false">false</option>
                              </Select>
                            ) : (
                              <TextField
                                fullWidth
                                label="Value"
                                type={
                                  definition.inputType === InputType.Number
                                    ? "number"
                                    : "text"
                                }
                                value={draft.value}
                                onChange={(e) =>
                                  updateSelection(definition.id, {
                                    value: e.currentTarget.value,
                                  })
                                }
                                className={clsx(
                                  error && "!border-red-600 focus:!ring-red-600"
                                )}
                              />
                            )}
                            {error && (
                              <p className="mt-1 text-xs text-red-500">{error}</p>
                            )}
                          </div>
                          <div className="flex items-center md:justify-end md:pt-6">
                            <Toggle
                              label="Visible to Customer?"
                              checked={draft.isVisibleToCustomer}
                              onChange={(value) =>
                                updateSelection(definition.id, {
                                  isVisibleToCustomer: value,
                                })
                              }
                            />
                          </div>
                        </div>

                        <p className="mt-2 text-xs text-gray-500">
                          {inputTypeOptions[definition.inputType]?.label ??
                            definition.inputType}
                          {" · "}
                          {settingTypeOptions[definition.settingType]?.label ??
                            definition.settingType}
                          {" · "}
                          {definition.required ? "Required" : "Optional"}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <Button
              onClick={createSettings}
              disabled={selectedDefinitions.length === 0 || createMany.isPending}
            >
              {createMany.isPending
                ? "Saving..."
                : selectedDefinitions.length > 1
                ? `Save ${selectedDefinitions.length} Settings`
                : "Save"}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
